import {
  Box,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  SliderMark,
  Tooltip,
} from "@chakra-ui/react";
import React from "react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { useStores } from "stores/setup/use-store";
import useWindowObserver from "hooks/useWindowObserver";

type TimeMarker = {
  index: number;
  label: string;
};

type TimelineRadarSliderProps = {
  totalFrames: number;
  currentIndex: number;
  pastLength: number;
  nowcastLength?: number;
  isNowcast?: boolean;
  timeMarkers: TimeMarker[];
  elapsedTime: string;
  onChange: (value: number) => void;
  onClick: () => void;
};

const TimelineRadarSlider: React.FC<TimelineRadarSliderProps> = ({
  totalFrames,
  currentIndex,
  pastLength,
  nowcastLength,
  isNowcast,
  timeMarkers,
  elapsedTime,
  onChange,
  onClick,
}) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "layer",
  });
  const {
    dataStores: { forecastChartMeteogramStore },
  } = useStores();
  useWindowObserver(".main-area");

  return (
    <Slider
      min={0}
      max={totalFrames - 1}
      value={currentIndex}
      onChange={onChange}
      onClick={onClick}
      step={1}
      flex="1"
    >
      {timeMarkers.map((marker, idx) => (
        <SliderMark
          key={idx}
          value={marker.index}
          mt="2"
          ml="-3"
          fontSize="xs"
          textAlign="center"
          whiteSpace="nowrap"
          color="#fff"
        >
          {marker.label}
        </SliderMark>
      ))}

      <Box
        position="absolute"
        right={0}
        textAlign="center"
        fontSize="xs"
        mt="-20px"
        zIndex={9999999}
        color={isNowcast ? "#ff9941" : "#fdd088"}
      >
        {t("nowcast")}
      </Box>

      <SliderTrack bg="#dbecf7" position="relative">
        <Box
          position="absolute"
          left={`${
            ((totalFrames - (nowcastLength || 0)) / totalFrames) * 100
          }%`}
          width={`${((nowcastLength || 0) / totalFrames) * 100}%`}
          height="100%"
          bg="#fdd088"
          zIndex={1}
        />

        {timeMarkers.map((marker, i) => (
          <Box
            key={i}
            position="absolute"
            left={`${(marker.index / (totalFrames - 1)) * 100}%`}
            top={0}
            bottom={0}
            width="2px"
            bg="#858585"
            zIndex={2}
          />
        ))}
        {nowcastLength && currentIndex > pastLength - 1 && (
          <Box
            position="absolute"
            left={`${((pastLength - 1) / (totalFrames - 1)) * 100}%`}
            width={`${((currentIndex - (pastLength - 1)) / (totalFrames - 1)) * 100}%`}
            height="100%"
            bg="#ff9941"
            zIndex={1}
          />
        )}

        <SliderFilledTrack bg="newBlue.500" />
      </SliderTrack>

      <Tooltip
        key={`${forecastChartMeteogramStore.openMeteogram}`}
        hasArrow
        bg={isNowcast ? "#fb9741" : "newBlue.500"}
        color="white"
        placement="top"
        isOpen={true}
        fontSize="12px"
        label={elapsedTime}
        borderRadius="20px"
        padding={"6px, 8px"}
        boxShadow="0 0 5px rgba(0, 0, 0, 0.5)"
      >
        <SliderThumb bg="#fff" />
      </Tooltip>
    </Slider>
  );
};

export default observer(TimelineRadarSlider);
